"use client";
import Link from "next/link";
import { ArrowUpRight, Globe2 } from "lucide-react";
import { useApp } from "./provider";
import { PageTitle } from "./shell";
export function Languages() {
  const { t, locale, languages } = useApp();
  return (
    <div className="container">
      <PageTitle
        eyebrow={t(
          "DIFFERENT LANGUAGES. SHARED CONNECTION.",
          "不同语言，共同连接。",
        )}
        title={t(
          "There’s a world in every sign.",
          "每一个手语，都有一个世界。",
        )}
        description={t(
          "Sign languages are distinct languages, shaped by the communities that use them. Explore each dictionary and its regional variations.",
          "手语是独立的语言，由使用它们的社区共同塑造。探索每一本词典和其中的地域变体。",
        )}
      />
      <div className="language-grid">
        {languages.map((l) => (
          <article className={`language-card language-${l.id}`} key={l.id}>
            <div className="language-card-topline">
              <strong>{l.id === "sgsl" ? "SgSL" : l.id.toUpperCase()}</strong>
              <span className="language-region">
                <Globe2 size={15} aria-hidden="true" />
                {l.id === "asl"
                  ? t("United States", "美国")
                  : l.id === "csl"
                    ? t("China", "中国")
                    : t("Singapore", "新加坡")}
              </span>
            </div>
            <h2>{locale === "en" ? l.en : l.zh}</h2>
            {l.words !== undefined && (
              <span className="language-count">
                {t(`${l.words} dictionary words`, `${l.words}个词条`)}
              </span>
            )}
            <p>
              {t(
                "Explore meanings and community-submitted variations. Your region’s signs belong here, too.",
                "探索词义与社区投稿的变体，您所在地区的手语也属于这里。",
              )}
            </p>
            <Link
              href={`/sign-languages/${encodeURIComponent(l.id)}`}
              className="button primary"
            >
              {t("Explore signs", "探索手语")}
              <ArrowUpRight size={16} />
            </Link>
          </article>
        ))}
      </div>
      <div className="notice">
        {t(
          "Additional sign languages can be added as communities join. Contact a platform administrator to help expand the dictionary.",
          "随着更多社区加入，我们可以添加更多手语语言。请联系平台管理员，帮助扩展词典。",
        )}
      </div>
    </div>
  );
}
export function About() {
  const { t } = useApp();
  return (
    <div className="container">
      <article className="prose">
        <div className="eyebrow">
          {t("OUR GUIDING PRINCIPLE", "我们的指导原则")}
        </div>
        <h1>
          {t(
            "Not one sign. A world of expression.",
            "不止一种手语，而是一个表达的世界。",
          )}
        </h1>
        <p>
          {t(
            "KnowYourSign is a living sign language lexicon powered by Deaf communities. Different countries, cities, and communities can use different signs for the same concept. We believe those differences deserve to be visible.",
            "KnowYourSign是由聋人社区共同建设的鲜活手语词典。不同国家、城市和社区可能使用不同的手语表达同一个概念。我们认为这些差异值得被看见。",
          )}
        </p>
        <h2>
          {t(
            "Popularity is a perspective, not a verdict.",
            "流行度是一种视角，而非定论。",
          )}
        </h2>
        <p>
          {t(
            "Likes help people find useful and widely used variations. They do not make one variation the only correct answer. Region, community, meaning, and context remain part of every contribution.",
            "点赞帮助人们发现实用且广泛使用的变体，但不会使某一种变体成为唯一正确答案。地区、社区、含义和语境始终是每份贡献的重要部分。",
          )}
        </p>
        <h2>{t("A dictionary that keeps growing", "持续成长的词典")}</h2>
        <p>
          {t(
            "Explore ASL, SgSL, and CSL in English and Chinese. Propose new words, share local variations, and help keep the lexicon useful. All submitted videos are reviewed before publication. Starter entries are vocabulary examples, not verified sign-language instruction.",
            "用中英文探索美国手语、新加坡手语和中国手语。提出新词、分享当地变体，帮助词典持续发挥作用。所有投稿视频都会在发布前经过审核。初始词条是词汇示例，并非经过验证的手语教学内容。",
          )}
        </p>
        <h2 id="guidelines">{t("Community guidelines", "社区准则")}</h2>
        <ul>
          <li>
            {t(
              "Respect regional differences and the people who use them.",
              "尊重地区差异以及使用不同手语的人。",
            )}
          </li>
          <li>
            {t(
              "Share videos you have the right to publish. Obtain consent from everyone shown.",
              "仅分享您有权发布的视频，并获得视频中每个人的同意。",
            )}
          </li>
          <li>
            {t(
              "Use clear definitions and explain your community context.",
              "使用清晰的释义，并说明社区背景。",
            )}
          </li>
          <li>
            {t(
              "Do not upload harassment, discriminatory content, or private information.",
              "请勿上传骚扰、歧视性内容或私人信息。",
            )}
          </li>
          <li>
            {t(
              "Report concerns constructively. Moderators may request changes, reject, or hide content.",
              "建设性地举报问题。审核员可以要求修改、拒绝或隐藏内容。",
            )}
          </li>
        </ul>
        <h2>
          {t("Recognition for meaningful contributions", "认可有意义的贡献")}
        </h2>
        <p>
          {t(
            "Earn 10 points for an approved video, 20 for an approved new word, 1 for a received like, and 5 for an accepted report. Points can be reversed when a like is removed or a video is hidden. Reputation recognizes contributions; it is not a language qualification. Cash incentives are not active.",
            "已批准的视频获得10点，已批准的新词获得20点，收到点赞获得1点，被采纳的举报获得5点。点赞撤销或视频隐藏时，相关点数可被撤回。声望用于认可贡献，不代表语言资质。目前尚未启用现金奖励。",
          )}
        </p>
        <h2 id="privacy">
          {t("Your profile, your choice", "您的资料，由您决定")}
        </h2>
        <p>
          {t(
            "Your email and phone number are not displayed publicly. You can choose whether your region and contribution list appear on your public profile. Published videos retain your display name and the regional information you submit with them. Moderators can access submissions for review.",
            "您的邮箱和手机号不会公开显示。您可以选择是否在公开资料中显示地区和贡献列表。已发布的视频保留您的显示名称及随视频提交的地区信息。审核员可以访问投稿以进行审核。",
          )}
        </p>
        <p>
          {t(
            "This initial project stores account details, moderation history, and contribution data to operate the service. A final retention policy and account-deletion process must be established by the operator before public launch.",
            "本项目初始版本存储账户资料、审核历史和贡献数据，以运行服务。运营方须在公开上线前制定最终的数据保留政策和账户删除流程。",
          )}
        </p>
        <Link href="/upload" className="button primary">
          {t("Share your sign", "分享您的手语")}
          <ArrowUpRight size={17} />
        </Link>
      </article>
    </div>
  );
}
